/**
 * WeekDayCell Component
 *
 * Week view cell container showing full booking list for a day.
 * This component renders ONLY the cell-container portion.
 * The parent component (BookingCard) handles the outer layout
 * with date-label and sum-label as siblings.
 *
 * @example
 * ```tsx
 * <WeekDayCell
 *   bookings={[
 *     { id: 1, heading: "8 timer", body: "PL-0197-136 - Aker BP - IO Reports" }
 *   ]}
 *   status="fullyBooked"
 *   onClick={handleClick}
 * />
 * ```
 */

import { BookingItem } from "@/components/booking/display/BookingItem";
import { DayCell } from "@/components/schedule/DayCell";
import { formatDecimal } from "@/utils/number/formatters";
import classNames from "classnames";
import React, { useCallback } from "react";
import styles from "./WeekDayCell.module.css";
import type { WeekDayCellProps } from "./types";

export const WeekDayCell: React.FC<WeekDayCellProps> = ({
  bookings,
  excludedHours,
  status = "free",
  colored = true,
  isWeekend = false,
  absenceFill = 0,
  selected = false,
  selectable = false,
  inRangePreview = false,
  disabled = false,
  onClick,
  className,
  "data-testid": testId,
  "data-component-name": componentName,
}) => {
  // Passes event for modifier key detection (shift, ctrl, meta)
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation(); // Prevent background click from closing sidebar
      if (!disabled && onClick) {
        onClick(e);
      }
    },
    [disabled, onClick],
  );

  // Keyboard handler - creates synthetic event for modifier key detection
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if ((e.key === "Enter" || e.key === " ") && !disabled && onClick) {
        e.preventDefault();
        e.stopPropagation();
        // Create a synthetic mouse event with keyboard modifiers
        const syntheticEvent = {
          shiftKey: e.shiftKey,
          ctrlKey: e.ctrlKey,
          metaKey: e.metaKey,
          stopPropagation: () => {},
          preventDefault: () => {},
        } as React.MouseEvent;
        onClick(syntheticEvent);
      }
    },
    [disabled, onClick],
  );

  const isInteractive = !disabled && onClick;

  return (
    <div
      className={classNames(styles.container, className)}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role={isInteractive ? "button" : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      data-disabled={disabled || undefined}
      data-interactive={isInteractive ? true : false}
      data-testid={testId}
      data-component-name={componentName}
    >
      <DayCell
        status={status}
        size="lg"
        colored={colored}
        isWeekend={isWeekend}
        absenceFill={absenceFill}
        selected={selected}
        selectable={selectable}
        inRangePreview={inRangePreview}
        data-component-name="day-cell"
      >
        <div data-component-name="booking-list">
          {bookings.length > 0 ? (
            bookings.map((booking) => (
              <BookingItem
                key={booking.id}
                heading={booking.heading}
                body={booking.body}
                type={booking.type}
              />
            ))
          ) : (
            <BookingItem empty />
          )}

          {/* Excluded bookings summary */}
          {excludedHours !== undefined && excludedHours > 0 && (
            <div data-component-name="excluded-summary">
              {formatDecimal(excludedHours)} timer på andre
            </div>
          )}
        </div>
      </DayCell>
    </div>
  );
};

WeekDayCell.displayName = "WeekDayCell";

export default WeekDayCell;
